import React, { memo } from "react";
import { Platform } from "react-native";

import ActivityFeed from "@/components/organisms/ActivityFeed";
import {
  Popover,
  PopoverBackdrop,
  PopoverContent,
} from "@/components/atoms/Popover";

/**
 * Props for HeaderActivityPopoverView component
 */
interface Props {
  isOpen: boolean;
  widthContent: number;
  onOpen: () => void;
  onClose: () => void;
  renderHeaderComponent: () => React.JSX.Element;
  renderTrigger: (triggerProps: object) => React.JSX.Element;
  isDark: boolean;
}

/**
 * Popover content surface size style.
 * design-system: dynamic-style waiver — data-driven value (measured content width)
 * @param widthContent - Measured content width in pixels
 */
const popoverContentStyle = (widthContent: number) => ({
  maxHeight: 540,
  width: widthContent,
  minWidth: 320,
});

/**
 * Pure view component for header activity notifications popover
 * Displays bell icon with unread count badge and activity feed
 * @param root0 - Component props
 * @param root0.isOpen - Whether popover is open
 * @param root0.widthContent - Width for popover content
 * @param root0.onOpen - Handler for popover open
 * @param root0.onClose - Handler for popover close
 * @param root0.renderHeaderComponent - Function to render header component
 * @param root0.renderTrigger - Memoized render function for popover trigger button
 * @param root0.isDark - Whether dark theme is active
 */
const HeaderActivityPopoverView = ({
  isOpen,
  widthContent,
  onOpen,
  onClose,
  renderHeaderComponent,
  renderTrigger,
  isDark,
}: Props) => (
  <Popover
    isOpen={isOpen}
    onOpen={onOpen}
    onClose={onClose}
    placement="bottom"
    offset={8}
    crossOffset={Platform.OS === "android" ? -100 : -60}
    size="lg"
    useRNModal
    trigger={renderTrigger}
  >
    <PopoverBackdrop />
    <PopoverContent
      className={`flex-1 rounded-md border-0 ${isDark ? "bg-surface-action" : "bg-white"}`}
      // eslint-disable-next-line no-restricted-syntax -- data-driven measured popover width (widthContent); PopoverContent is a bare re-export atom (no UNSAFE_style prop) and maxHeight 540 is off the sizing ladder
      style={popoverContentStyle(widthContent)}
    >
      {renderHeaderComponent()}
      <ActivityFeed
        onClosePopover={onClose}
        isOpenOnPopover
        isShowHeader={false}
        isDark={isDark}
      />
    </PopoverContent>
  </Popover>
);

HeaderActivityPopoverView.displayName = "HeaderActivityPopoverView";

export default memo(HeaderActivityPopoverView);
